import {
  Box,
  FormField,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import useRealtimeDoc from 'hooks/use-realtimeDoc';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import getProcessLogRef from '../api/getProcessLogRef';
import updateProcessLog from '../api/updateProcessLog';

/**
 * Realtime panel for the active process_logs run session: run details
 * (full part id incl. material, run number, and step part / cap material
 * when set) plus a debounced-editable Observations field. The PLC holds
 * the session id — a blank/whitespace id (skipped session) renders the
 * empty state.
 */
export default function ProcessLogPanel({ processLogId = null }) {
  const trimmedId = useMemo(() => processLogId?.trim() || null, [processLogId]);

  const [observations, setObservations] = useState('');

  const docRef = useMemo(() => getProcessLogRef(trimmedId), [trimmedId]);
  const { data: processLog } = useRealtimeDoc({ docRef });

  useEffect(() => {
    setObservations(processLog?.observations || '');
  }, [processLog?.observations]);

  const debouncedUpdateFirebase = useMemo(
    () => debounce((newValue) => {
      if (!trimmedId) {
        return;
      }

      updateProcessLog(trimmedId, {
        observations: newValue,
      });
    }, 1000),
    [trimmedId],
  );

  useEffect(() => () => {
    debouncedUpdateFirebase.cancel();
  }, [debouncedUpdateFirebase]);

  const details = useMemo(() => {
    if (!processLog) {
      return [
        { label: 'Project', value: 'No active session' },
        { label: 'Number', value: 'No active session' },
      ];
    }
    const pairs = [
      { label: 'Project', value: [processLog.project, processLog.pPart].filter(Boolean).join('-') },
      { label: 'Number', value: processLog.number },
    ];
    if (processLog.stepPPartId) {
      pairs.push({ label: 'Step Part', value: processLog.stepPPartId });
    }
    if (processLog.capMaterial) {
      pairs.push({ label: 'Cap Material', value: processLog.capMaterial });
    }
    return pairs;
  }, [processLog]);

  return (
    <SpaceBetween size="m">
      {/* Plain grid instead of Cloudscape ColumnLayout: this panel sits in a
          narrow HMI grid cell where ColumnLayout collapses to one column. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 8px' }}>
        {details.map(({ label, value }) => (
          <div key={label}>
            <Box variant="awsui-key-label">{label}</Box>
            <div>{value}</div>
          </div>
        ))}
      </div>
      {processLog?.currentStepNote ? (
        <div>
          <Box variant="awsui-key-label">Step Note</Box>
          <div style={{ whiteSpace: 'pre-wrap' }}>{processLog.currentStepNote}</div>
        </div>
      ) : null}
      <div>
        <Box variant="awsui-key-label">Hypothesis</Box>
        <div style={{ whiteSpace: 'pre-wrap' }}>{processLog ? processLog.hypothesis : 'No active session'}</div>
      </div>

      <FormField label="Observations">
        <Textarea
          rows={4}
          disabled={!processLog}
          value={observations}
          onChange={({ detail }) => {
            setObservations(detail.value);
            debouncedUpdateFirebase(detail.value);
          }}
        />
      </FormField>
    </SpaceBetween>
  );
}

ProcessLogPanel.propTypes = {
  processLogId: PropTypes.string,
};
